import { useEffect, useState } from "react";
import supabase from "../config/supabaseConfig";

// Define types
export interface Category {
  id: string;
  name: string;
  image: string;
  adCount?: number;
}

export interface Subcategory {
  id: string;
  name: string;
  image: string;
  category_id: string;
  adCount?: number;
  parentCategoryName?: string;
}

interface CategoriesData {
  categories: Category[];
  subcategories: Subcategory[];
}

// Several components on the same page use this hook (sidebar, mobile cards, slider,
// menu...). They now share one download instead of each fetching its own copy.
// Kept for a few minutes so the ad counts don't go stale for long.
const CACHE_MS = 5 * 60 * 1000;
let cache: { at: number; promise: Promise<CategoriesData> } | null = null;

const fetchCategoriesAndCounts = async (): Promise<CategoriesData> => {
  // Count active ads per category/subcategory server-side via RPC
  // instead of fetching every active ad's category_id/subcategory_id
  // client-side. The old approach pulled one row per active ad with
  // no .limit(), so counts would silently fall short (and the
  // request would keep growing) as the ads table scales past the
  // default page size. count_ads_by_category/count_ads_by_subcategory
  // mirror the existing count_ads_by_state/count_ads_by_city RPCs
  // used in useFetchAllLocations.ts.
  const [
    { data: categoriesData, error: categoriesError },
    { data: subcategoriesData, error: subcategoriesError },
    { data: categoryAdCounts, error: categoryCountError },
    { data: subcategoryAdCounts, error: subcategoryCountError },
  ] = await Promise.all([
    supabase.from('categories').select('*'),
    supabase.from('subcategories').select('*'),
    supabase.rpc('count_ads_by_category'),
    supabase.rpc('count_ads_by_subcategory'),
  ]);

  if (categoriesError) throw categoriesError;
  if (subcategoriesError) throw subcategoriesError;

  if (categoryCountError || subcategoryCountError) {
    console.error("Error fetching ad counts:", categoryCountError || subcategoryCountError);
  }

  // Build maps of counts for easier lookup
  const categoryCountMap: Record<string, number> = {};
  const subcategoryCountMap: Record<string, number> = {};

  categoryAdCounts?.forEach((row: { category_id: string; count: number }) => {
    if (row.category_id) categoryCountMap[row.category_id] = row.count;
  });
  subcategoryAdCounts?.forEach((row: { subcategory_id: string; count: number }) => {
    if (row.subcategory_id) subcategoryCountMap[row.subcategory_id] = row.count;
  });

  // Combine the data with counts
  const categoriesWithCounts = (categoriesData || []).map(category => ({
    ...category,
    adCount: categoryCountMap[category.id] || 0
  }));

  // Add counts and parent category names to subcategories for display purposes
  const subcategoriesWithCounts = (subcategoriesData || []).map(subcat => ({
    ...subcat,
    adCount: subcategoryCountMap[subcat.id] || 0,
    parentCategoryName: categoriesWithCounts.find(cat => cat.id === subcat.category_id)?.name
  }));

  return { categories: categoriesWithCounts, subcategories: subcategoriesWithCounts };
};

const loadCategories = (): Promise<CategoriesData> => {
  if (cache && Date.now() - cache.at < CACHE_MS) return cache.promise;
  const promise = fetchCategoriesAndCounts().catch((err) => {
    cache = null; // don't keep a failure: the next component tries again
    throw err;
  });
  cache = { at: Date.now(), promise };
  return promise;
};

const useFetchAllCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadCategories()
      .then((data) => {
        if (cancelled) return;
        setCategories(data.categories);
        setSubcategories(data.subcategories);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { categories, subcategories, loading, error };
};

export default useFetchAllCategories;
